import Loading from '@/shared/components/Carregando';
import {
  createAttendanceSheet,
  listAllMonthsOfAnIntership,
  sendNotification,
} from '@/modules/business/attendance-sheet/attendance-sheet.service';
import ModalConflitos, { type ValidacaoConflitos } from '@/modules/business/lotacoes/ModalConflitos';
import { EditLotacaoCard } from '@/modules/business/lotacoes/editLotacaoCard';
import { IOpcaoPredio } from '@/modules/business/lotacoes/hooks/useDadosVinculo';
import { LotacaoService } from '@/modules/business/lotacoes/lotacao.service';
import { TipoPessoaLotada } from '@/modules/business/lotacoes/enums/tipo-pessoa-lotada.enum';
import { TipoLotacao } from '@/modules/business/lotacoes/enums/tipo-lotacao.enum';
import { GetDefensorias } from '@/modules/structure/defensoria/defensoria.service';
import { GetNucleos } from '@/modules/structure/nucleo/nucleo.service';
import { GetPredios } from '@/modules/structure/predio/predio.service';
import { readEstagiario } from '@/modules/people/estagiario.service';
import { readPersonResponsibleSelect } from '@/modules/people/person.service';
import { CreateLotacaoDto, ReadLotacaoDto } from '@/api.types';
import { DataAmigavel, DataIso } from '@/shared/utils/datas';
import { IOpcao, SelectOption } from '@/shared/types/selectOption';
import { WizardStepActions } from '@/shared/components/forms/WizardStepActions';
import { CarregarPermissoes, ValidarPermissao } from '@/modules/system/auth/application/auth.service';
import { PermissionEnum } from '@/modules/system/auth/domain/enums/permission.enum';
import { RoleEnum } from '@/modules/system/auth/domain/enums/role.enum';
import { isAxiosError } from 'axios';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { SelectInstance } from 'react-select';
import { toast } from 'react-toastify';

type Props = {
  estagiarioId: number;
  estagioId: number;
  nomeEstagiario?: string;
  onBack: () => void;
  onSuccess: () => void;
};

export function LotacaoEstagiarioWizardStep({
  estagiarioId,
  estagioId,
  nomeEstagiario,
  onBack,
  onSuccess,
}: Props) {
  const lotacaoService = useMemo(() => new LotacaoService(), []);
  const { register, handleSubmit, reset, getValues, setValue } = useForm<ReadLotacaoDto>();
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [modalConflitos, setModalConflitos] = useState(false);
  const [conflitos, setConflitos] = useState<ValidacaoConflitos | null>(null);
  const [criarFolhaMesAtual, setCriarFolhaMesAtual] = useState(true);
  const [modalNotificarEmail, setModalNotificarEmail] = useState(false);
  const [lotacaoPendente, setLotacaoPendente] = useState<CreateLotacaoDto | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const permissoes = useMemo(() => CarregarPermissoes(), []);
  const permissaoCriarFolhas = ValidarPermissao(permissoes, [
    { role: RoleEnum.FOLHA, level: PermissionEnum.CREATE },
  ]);

  const mesAtualFolha = useMemo(() => {
    const hoje = new Date();
    return {
      ano: hoje.getFullYear(),
      mes: hoje.getMonth() + 1,
      rotulo: hoje.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' }),
    };
  }, []);

  const refSelectEstagiario = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectEstagio = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectDefensoria = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectNucleo = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectPredio = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectSetor = useRef<SelectInstance<IOpcao | null>>(null);
  const refSelectResponsavel = useRef<SelectInstance<SelectOption | null>>(null);

  const [colaboradores] = useState<IOpcao[]>([]);
  const [defensores] = useState<IOpcao[]>([]);
  const [defensorias, setDefensorias] = useState<IOpcao[]>([]);
  const [estagiarios, setEstagiarios] = useState<IOpcao[]>([]);
  const [pessoas, setPessoas] = useState<SelectOption[]>([]);
  const [estagios, setEstagios] = useState<IOpcao[]>([]);
  const [nucleos, setNucleos] = useState<IOpcao[]>([]);
  const [predios, setPredios] = useState<IOpcaoPredio[]>([]);
  const [setoresDisponiveis, setSetoresDisponiveis] = useState<IOpcao[]>([]);

  useEffect(() => {
    if (estagiarioId <= 0 || estagioId <= 0) {
      setLoaded(true);
      return;
    }

    let cancelled = false;
    setLoaded(false);

    const carregar = async () => {
      try {
        const [defensoriasRes, nucleosRes, prediosRes, pessoasRes, estagiarioRes] = await Promise.all([
          GetDefensorias(0, 1000),
          GetNucleos(0, 1000),
          GetPredios(0, 1000),
          readPersonResponsibleSelect(),
          readEstagiario({ id: estagiarioId, skip: 0, take: 1, comEstagios: true }),
        ]);

        if (cancelled) return;

        setDefensorias(defensoriasRes.objects.map((item) => ({ value: item.id, label: item.nome })));
        setNucleos(nucleosRes.objects.map((item) => ({ value: item.id, label: item.nome })));
        setPredios(
          prediosRes.objects.map(
            (item) =>
              ({
                value: item.id,
                label: item.nome,
                setores: item.setores,
              }) as IOpcaoPredio,
          ),
        );
        setSetoresDisponiveis([]);

        const pessoasOptions = pessoasRes[0].map((item) => ({ value: item.id, label: item.nome }));
        pessoasOptions.unshift({ label: '-', value: '' });
        setPessoas(pessoasOptions);

        const estagiarioRegistro = estagiarioRes[0]?.[0];
        const nomeExibicao = nomeEstagiario ?? estagiarioRegistro?.pessoa?.nome ?? 'Estagiário';
        const estagiarioOption = { value: estagiarioId, label: nomeExibicao };
        setEstagiarios([estagiarioOption]);

        const listaEstagios =
          estagiarioRegistro?.estagios?.map((item) => ({
            value: item.id,
            label: `${DataAmigavel(item.data_inicio_estagio)} - ${TipoLotacao[item.tipo_estagio]}`,
          })) ?? [];
        setEstagios(listaEstagios);

        const estagioOption =
          listaEstagios.find((e) => e.value === estagioId) ??
          (estagioId > 0 ? { value: estagioId, label: `Estágio #${estagioId}` } : null);

        reset({
          tipo: TipoLotacao.Designacao,
          ativo: true,
          estagio: {
            id: estagioId,
            estagiario: {
              id: estagiarioId,
              pessoa: { id: '', nome: nomeExibicao },
              ativo: true,
            },
          },
        } as ReadLotacaoDto);

        requestAnimationFrame(() => {
          if (cancelled) return;
          refSelectEstagiario.current?.setValue(estagiarioOption, 'select-option');
          if (estagioOption) {
            refSelectEstagio.current?.setValue(estagioOption, 'select-option');
          }
        });
      } catch (error) {
        if (!cancelled) {
          toast.error(error instanceof Error ? error.message : 'Falha ao carregar dados da lotação.');
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };

    void carregar();

    return () => {
      cancelled = true;
    };


  }, [estagiarioId, estagioId, nomeEstagiario]);

  const handleSelectChangePessoa = useCallback(
    async (
      selectedOption: IOpcao | null,
      options: IOpcao[],
      field: 'defensor' | 'colaborador' | 'estagiario' | 'estagio',
    ) => {
      if (field === 'estagiario' || field === 'estagio') return;

      if (selectedOption) {
        const selectedItem = options.find((option) => option.value === selectedOption.value);
        if (selectedItem) {
          setValue(field as 'defensor' | 'colaborador', {
            id: selectedItem.value,
            pessoa: { id: '', nome: selectedItem.label },
            ativo: true,
          });
        }
      } else {
        setValue(field as 'defensor' | 'colaborador', undefined);
      }
    },
    [setValue],
  );

  const handleSelectChangeLocal = useCallback(
    (
      selectedOption: IOpcao | null,
      options: IOpcao[],
      field: 'defensoria' | 'nucleo' | 'predio' | 'setor',
    ) => {
      if (field === 'predio') {
        const predioSelecionado = selectedOption
          ? predios.find((predio) => predio.value === selectedOption.value)
          : null;

        const setoresAssociados = predioSelecionado?.setores ?? [];
        setSetoresDisponiveis(
          setoresAssociados.map((setor) => ({
            value: setor.id,
            label: setor.nome,
          })),
        );

        refSelectSetor.current?.setValue(null, 'deselect-option');
        setValue('setor', undefined);
        return;
      }

      if (selectedOption) {
        const selectedItem = options.find((option) => option.value === selectedOption.value);
        if (selectedItem) {
          const newValue = { id: selectedItem.value, nome: selectedItem.label };
          setValue(field as 'defensoria' | 'nucleo' | 'setor', newValue);

          if (field === 'defensoria') {
            refSelectNucleo.current?.setValue(null, 'deselect-option');
            refSelectSetor.current?.setValue(null, 'deselect-option');
            refSelectPredio.current?.setValue(null, 'deselect-option');
            setValue('nucleo', undefined);
            setValue('setor', undefined);
          } else if (field === 'nucleo') {
            refSelectDefensoria.current?.setValue(null, 'deselect-option');
            setValue('defensoria', undefined);
          }
        }
      } else {
        setValue(field as 'defensoria' | 'nucleo' | 'setor', undefined);
      }
    },
    [predios, setValue],
  );

  const handleSelectChangeDados = useCallback(
    (
      selectedOption: SelectOption | null,
      options: SelectOption[],
      field: 'responsavel',
    ) => {
      if (selectedOption) {
        const selectedItem = options.find((option) => option.value === selectedOption.value);
        if (selectedItem) {
          setValue(field, { id: selectedItem.value, nome: selectedItem.label });
        }
      } else {
        setValue(field, undefined);
      }
    },
    [setValue],
  );

  const onSubmit: SubmitHandler<CreateLotacaoDto> = async (dados) => {
    const obj: CreateLotacaoDto = { ...dados };

    const localSelecionado = (
      ref: { current: SelectInstance<IOpcao | null> | null },
    ) => {
      const atual = ref.current?.getValue()?.[0];
      if (atual?.value == null || atual.value === 0) return undefined;
      return { id: Number(atual.value), nome: String(atual.label ?? '') };
    };

    const responsavelSelecionado = refSelectResponsavel.current?.getValue()?.[0];
    if (!obj.defensoria?.id) obj.defensoria = localSelecionado(refSelectDefensoria);
    if (!obj.nucleo?.id) obj.nucleo = localSelecionado(refSelectNucleo);
    if (!obj.setor?.id) obj.setor = localSelecionado(refSelectSetor);
    if (!obj.responsavel?.id && responsavelSelecionado?.value) {
      obj.responsavel = { id: String(responsavelSelecionado.value) };
    }

    if (!obj.defensoria?.id && !obj.nucleo?.id && !obj.setor?.id) {
      toast.error('Informe a defensoria, o núcleo ou o setor.');
      return;
    }

    if (!obj.setor?.id) obj.setor = undefined;

    if (obj.defensoria?.id) {
      delete obj.nucleo;
    } else if (obj.nucleo?.id) {
      delete obj.defensoria;
    }

    if ('predio' in obj) {
      delete (obj as Record<string, unknown>).predio;
    }

    obj.data_inicial =
      obj.data_inicial && obj.data_inicial.length >= 10 ? DataIso(obj.data_inicial) : undefined;
    obj.data_final =
      obj.data_final && obj.data_final.length >= 10 ? DataIso(obj.data_final) : undefined;

    if (obj.entrada === '') delete obj.entrada;
    if (obj.saida === '') delete obj.saida;

    if (!obj.estagio?.id) {
      obj.estagio = { id: estagioId };
    }

    setLotacaoPendente(obj);
    setModalNotificarEmail(true);
  };

  const obterOuCriarFolhaMesAtual = async (): Promise<string | null> => {
    try {
      const folhaId = await createAttendanceSheet(estagioId, mesAtualFolha.ano, mesAtualFolha.mes);
      toast.success('Folha de frequência do mês criada com sucesso.');
      return folhaId || null;
    } catch (error) {
      const mensagem = String(error);
      if (mensagem.toLowerCase().includes('já existe')) {
        toast.info('A folha de frequência do mês já existia.');
        try {
          const meses = await listAllMonthsOfAnIntership(estagioId);
          const existente = meses.find(
            (item) => item.ano === mesAtualFolha.ano && item.mes === mesAtualFolha.mes && item.folha?.id,
          );
          return existente?.folha?.id ?? null;
        } catch {
          return null;
        }
      }

      toast.warning(
        `Vínculo cadastrado, mas não foi possível criar a folha: ${mensagem}`,
      );
      return null;
    }
  };

  const notificarEstagiarioPorEmail = async (folhaId: string) => {
    try {
      const resultado = await sendNotification(folhaId, { incluirBoasVindas: true });
      toast.success(resultado ? String(resultado) : 'Notificação enviada por e-mail.');
    } catch (error) {
      toast.warning(
        `Lotação concluída, mas não foi possível notificar o estagiário: ${String(error)}`,
      );
    }
  };

  const executarCadastro = async (obj: CreateLotacaoDto, notificarPorEmail: boolean) => {
    setModalNotificarEmail(false);
    setLotacaoPendente(null);
    setIsLoading(true);

    try {
      const res = await lotacaoService.Create(obj);

      if (isNaN(res as number)) {
        toast.warning(String(res));
        return;
      }

      toast.success('Vínculo de estagiário cadastrado com sucesso.');

      let folhaId: string | null = null;
      const deveCriarOuLocalizarFolha =
        (criarFolhaMesAtual && permissaoCriarFolhas) || notificarPorEmail;

      if (deveCriarOuLocalizarFolha) {
        folhaId = await obterOuCriarFolhaMesAtual();
      }

      if (notificarPorEmail) {
        if (folhaId) {
          await notificarEstagiarioPorEmail(folhaId);
        } else {
          toast.warning(
            'Lotação concluída, mas não foi possível localizar a folha para notificar o estagiário.',
          );
        }
      }

      onSuccess();
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 400) {
        const errorData = error.response.data as ValidacaoConflitos | undefined;
        if (errorData?.conflitos && Array.isArray(errorData.conflitos)) {
          setConflitos(errorData);
          setModalConflitos(true);
          return;
        }
      }
      toast.error(String(error));
    } finally {
      setIsLoading(false);
    }
  };

  if (!loaded) return <Loading />;

  return (
    <Fragment>
      {nomeEstagiario && (
        <p className="text-muted small mb-3">
          {'Lotação do estagiário'} <strong>{nomeEstagiario}</strong>
        </p>
      )}

      <form
        ref={formRef}
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
      >
        <EditLotacaoCard
          form={{ register, getValues }}
          tipoPessoaLotada={TipoPessoaLotada.Estagiario}
          pessoa={{
            colaboradores,
            defensores,
            estagiarioid: estagiarioId,
            estagiarios,
            estagioid: estagioId,
            estagios,
            refSelectColaborador: { current: null },
            refSelectDefensor: { current: null },
            refSelectEstagiario,
            refSelectEstagio,
            handleSelectChange: handleSelectChangePessoa,
          }}
          dados={{
            pessoas,
            refSelectResponsavel,
            handleSelectChange: handleSelectChangeDados,
          }}
          local={{
            defensorias,
            nucleos,
            predios: predios as unknown as IOpcao[],
            setoresDisponiveis,
            refSelectDefensoria,
            refSelectNucleo,
            refSelectPredio,
            refSelectSetor,
            handleSelectChange: handleSelectChangeLocal,
          }}
        />

        {permissaoCriarFolhas && (
          <div className="mt-3 mb-2">
            <Form.Check
              type="checkbox"
              id="criar-folha-mes-atual"
              label={`Criar folha de frequência de ${mesAtualFolha.rotulo}`}
              checked={criarFolhaMesAtual}
              onChange={(e) => setCriarFolhaMesAtual(e.target.checked)}
            />
          </div>
        )}

        <WizardStepActions
          onBack={onBack}
          primaryLabel={'Concluir lotação'}
          primaryLoadingLabel="Cadastrando..."
          isLoading={isLoading}
        />
      </form>

      <Modal
        show={modalNotificarEmail}
        centered
        onHide={() => {
          if (isLoading) return;
          setModalNotificarEmail(false);
          setLotacaoPendente(null);
        }}
      >
        <Modal.Header closeButton={!isLoading}>
          <Modal.Title>{'Notificar estagiário'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {'Deseja notificar o estagiário'}
            {nomeEstagiario ? (
              <>
                {' '}
                <strong>{nomeEstagiario}</strong>
              </>
            ) : null}{' '}
            {'por e-mail com as boas-vindas pela matrícula e a folha de frequência do mês?'}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            type="button"
            disabled={isLoading || !lotacaoPendente}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (!lotacaoPendente) return;
              void executarCadastro(lotacaoPendente, false);
            }}
          >
            {'Não notificar'}
          </Button>
          <Button
            variant="primary"
            type="button"
            disabled={isLoading || !lotacaoPendente}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (!lotacaoPendente) return;
              void executarCadastro(lotacaoPendente, true);
            }}
          >
            {isLoading ? 'Aguarde...' : 'Sim, notificar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <ModalConflitos
        show={modalConflitos}
        handleHide={() => setModalConflitos(false)}
        conflitos={conflitos}
      />
    </Fragment>
  );
}
